import { Delete, Draw, Undo } from "@mui/icons-material";
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { MenuSelect } from "components";
import NoDetectionModels from "components/NoDetectionModels";
import Polygon from "components/Polygon";
import { AI_MODEL_LABELS, filterModelsForUI } from "config/constants";
import { useKeyDown } from "hooks";
import useTranslation from "hooks/useTranslation";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, Image as KonvaImage, Layer, Line, Stage } from "react-konva";
import { CameraModel, IPoint, IPolygon, ModelTypes } from "types";
import UploadPolygons from "./upload";

interface IDrawingStage {
  imgUrl: string;
  camera: CameraModel;
  onReload?: (args?: any) => any;
}

export default function DrawingStage({ imgUrl, camera, onReload }: IDrawingStage) {
  /* ---------------------------------- Hooks --------------------------------- */
  const [{ vectorH, vectorW }, setVectorDimensions] = useState({
    vectorH: 500,
    vectorW: 500
  });
  const [scale, setScale] = useState<number>(0);
  const [image, setImage] = useState<HTMLImageElement>();
  const [loadingImage, setLoadingImage] = useState(false);
  const [isDrawing, setDrawing] = useState(false);
  const [mousePosition, setMousePosition] = useState<IPoint>();
  const [polygons, setPolygons] = useState<IPolygon[]>([]);
  const [selected, setSelected] = useState<IPolygon>();
  const [currentPolygons, setCurrentPolygons] = useState<IPolygon[]>([]);
  const [points, setPoints] = useState<IPoint[]>([]);

  /* ---------------------------------- refs ---------------------------------- */
  const selectedRef = useRef<IPolygon>();
  const stageRef = useRef<any>(null);
  const counterRef = useRef(-1);
  const toggleDraw = useCallback(() => setDrawing((prevState) => !prevState), []);

  const t = useTranslation();
  const enabledModels = useMemo(
    () => (camera.box?.site ? filterModelsForUI(camera.box.site.enabledModels) : []),
    [camera.box?.site]
  );
  const defaultZoneModels = useMemo<ModelTypes[]>(
    () => (enabledModels.includes("person") ? ["person"] : enabledModels.slice(0, 1)),
    [enabledModels]
  );

  const onDelete = useCallback(() => {
    if (!selected) return;
    setCurrentPolygons((prevState) => prevState.filter((item) => item.id !== selected.id));
  }, [selected]);

  const onChangePoints = useCallback((points: IPoint[], polygon?: IPolygon) => {
    setSelected((prevState) =>
      prevState
        ? {
            ...prevState,
            points
          }
        : prevState
    );
    if (polygon) {
      setCurrentPolygons((prevState) =>
        prevState.map((item) =>
          item.id === polygon.id
            ? {
                ...item,
                points
              }
            : item
        )
      );
    }
  }, []);

  const onChangeModels = useCallback((models: ModelTypes[], polygon?: IPolygon) => {
    setSelected((prevState) =>
      prevState
        ? {
            ...prevState,
            models
          }
        : prevState
    );
    if (polygon) {
      setCurrentPolygons((prevState) =>
        prevState.map((item) =>
          item.id === polygon.id
            ? {
                ...item,
                models
              }
            : item
        )
      );
    }
  }, []);

  const onRemoveLastPoint = useCallback(() => setPoints((prevState) => prevState.slice(0, prevState.length - 1)), []);

  const onMouseEnterStage = useCallback(() => {
    const stage = stageRef.current;
    const point = stage.getPointerPosition();

    if (point && isDrawing) {
      setMousePosition(point);
    }
  }, [isDrawing]);

  const onMouseLeaveStage = useCallback(() => {
    setMousePosition(undefined);
  }, []);

  const onClickStage = useCallback(() => {
    // console.log("onClickStage")
    const stage = stageRef.current;
    const point = stage.getPointerPosition();
    if (point && isDrawing) {
      if (points.length === 3) {
        setPoints([]);
        const newPolygon: IPolygon = {
          id: counterRef.current,
          models: defaultZoneModels,
          points: [...points, point]
        };
        // console.log("adding new polygon", newPolygon);
        setCurrentPolygons((prevState) => [...prevState, newPolygon]);
        setSelected(newPolygon);
        counterRef.current -= 1;
      } else {
        setPoints([...points, point]);
      }
    }
  }, [points, isDrawing, defaultZoneModels]);

  const onUndo = useCallback(() => {
    setPoints([]);
    setCurrentPolygons(polygons);
  }, [polygons]);

  useEffect(() => {
    selectedRef.current = selected;
    setDrawing(false);
  }, [selected]);

  useEffect(() => {
    if (currentPolygons && currentPolygons.length > 0) {
      const newResult = currentPolygons.find((item) => item.id === selectedRef.current?.id);
      if (newResult) {
        setSelected(newResult);
      } else {
        setSelected(currentPolygons[0]);
      }
    } else {
      setSelected(undefined);
    }
  }, [currentPolygons]);

  useEffect(() => {
    // console.log("result", imgUrl);
    setLoadingImage(true);
    let image = new Image();
    image.src = imgUrl;
    image.onload = () => {
      setLoadingImage(false);
      setImage(image);
      const naturalHeight = image.height;
      const naturalWidth = image.width;
      var maxWidth = 500;
      var maxHeight = 500;
      const parentElement = document.getElementById("parent-image-container");
      if (parentElement) {
        maxWidth = parentElement.clientWidth;
        maxHeight = (window.innerHeight - parentElement.offsetTop) * 0.8;
      }
      let scale = maxWidth / naturalWidth;
      if (naturalHeight * scale > maxHeight) {
        scale = maxHeight / naturalHeight;
      }
      setScale(scale);
      setVectorDimensions({
        vectorH: naturalHeight * scale,
        vectorW: naturalWidth * scale
      });
      const newPolygons: IPolygon[] = [];

      camera?.polygons?.forEach((item) =>
        newPolygons.push({
          id: Math.random(),
          points: item?.points?.map(({ x, y }) => ({ x: (x || 0) * scale, y: (y || 0) * scale })) || [],
          models: item.models || []
        })
      );

      setPolygons(newPolygons);
      setCurrentPolygons(newPolygons);
    };
  }, [camera, imgUrl]);

  useKeyDown(
    useCallback(
      (event) => {
        // console.log(event.key)
        switch (event.key) {
          case "Escape":
            if (isDrawing) {
              onRemoveLastPoint();
            }
            break;
          case "z":
            if (event.ctrlKey && isDrawing) {
              onRemoveLastPoint();
            }
            break;
          default:
            break;
        }
      },
      [onRemoveLastPoint, isDrawing]
    )
  );

  return (
    <Fragment>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t["Add Zone"]}>
            <IconButton onClick={toggleDraw} color={isDrawing ? "warning" : undefined}>
              <Draw />
            </IconButton>
          </Tooltip>
          <Tooltip title={t["Undo Changes"]}>
            <IconButton onClick={onUndo}>
              <Undo />
            </IconButton>
          </Tooltip>
          {selected && (
            <Tooltip title={t["Delete Zone"]}>
              <IconButton onClick={onDelete} color="error">
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <UploadPolygons scale={scale} cameraId={camera?._id || 0} polygons={currentPolygons} onReload={onReload} />
        </Stack>
      </Box>
      <Box id="parent-image-container" position="relative" overflow="hidden" width="100%" mb={5}>
        {loadingImage ? (
          <Skeleton variant="rectangular" width="100%" height="50vh" />
        ) : (
          <Stage
            style={{ textAlign: "center" }}
            ref={stageRef}
            width={vectorW}
            height={vectorH}
            onClick={onClickStage}
            onTap={onClickStage}
            onMouseEnter={onMouseEnterStage}
            onMouseMove={onMouseEnterStage}
            onMouseLeave={onMouseLeaveStage}
          >
            <Layer>
              <KonvaImage image={image} width={vectorW} height={vectorH} />
              {selected &&
                currentPolygons.map((polygon) => (
                  <Polygon
                    key={polygon.id}
                    points={polygon.points}
                    maxX={vectorW}
                    maxY={vectorH}
                    isSelected={selected?.id === polygon.id}
                    onChange={(points) => onChangePoints(points, polygon)}
                    lineProps={{
                      onClick: () => setSelected(polygon),
                      onTap: () => setSelected(polygon)
                    }}
                  />
                ))}
              {points.map(({ x, y }, index) => (
                <Circle key={index} x={x} y={y} radius={5} fill="lightblue" />
              ))}
              {points.length > 1 && (
                <Line points={points.map((item) => [item.x, item.y]).flatMap((item) => item)} stroke="black" />
              )}
              {mousePosition && points.length > 0 && (
                <Line
                  points={[mousePosition, points[points.length - 1]]
                    .map((item) => [item.x, item.y])
                    .flatMap((item) => item)}
                  stroke="black"
                />
              )}
            </Layer>
          </Stage>
        )}
      </Box>
      {selected && (
        <Stack
          direction={{ sm: "row" }}
          alignItems="center"
          justifyContent={{ xs: "center", sm: "flex-start" }}
          width="100%"
          flexWrap="wrap"
        >
          <Typography fontWeight="bold">For AI Models:&nbsp;</Typography>
          {enabledModels.length === 0 ? (
            <Box width="100%" mt={1}>
              <NoDetectionModels />
            </Box>
          ) : (
            <MenuSelect
              multiple
              disableClearable
              value={(selected.models || []).filter((model) => enabledModels.includes(model))}
              renderItem={(elm) => t[AI_MODEL_LABELS[elm]]}
              maxHeight={200}
              options={enabledModels}
              onChange={(v) => {
                onChangeModels(
                  v.filter((model) => enabledModels.includes(model)),
                  selected
                );
              }}
            >
              {(selected.models || []).some((model) => enabledModels.includes(model))
                ? (selected.models || [])
                    .filter((model) => enabledModels.includes(model))
                    .map((elm) => t[AI_MODEL_LABELS[elm]])
                    .join(", ")
                : "No Models Selected"}
            </MenuSelect>
          )}
        </Stack>
      )}
    </Fragment>
  );
}
